import { useRef } from "react"
import { CreateAreaSidebar } from "components/ui/customWebviewer/shared/CreateAreaSidebar/CreateAreaSidebar"
import { FurnitureImage } from "components/ui/customWebviewer/FurnitureSidebar/layout/FurnitureImage/FurnitureImage"
import { ElevationReferenceSelect } from "components/ui/customWebviewer/FurnitureSidebar/layout/ElevationReferenceSelect/ElevationReferenceSelect"
import { DimensionsSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/DimensionsSection/DimensionsSection"
import { NameSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/NameSection/NameSection"
import { PriceSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/PriceSection/PriceSection"
import { CategoriesSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/CategoriesSection/CategoriesSection"
import { CustomizationSection } from "components/ui/customWebviewer/shared/CustomizationSection/CustomizationSection"
import {
	OTHER_TOP_LEVEL_CATEGORY,
	OTHER_TOP_LEVEL_OPTIONS,
} from "components/ui/customWebviewer/FurnitureSidebar/furniture.constants"
import { useCreateOtherForm } from "./useCreateOtherForm.hook"
import type { TOtherCreateDetails } from "components/ui/customWebviewer/interactions/useOtherCreate"
import type { TEref } from "types/elevationReferences.type"
import type { DefaultOptionType } from "antd/es/select"
import style from "./CreateOtherSidebar.module.scss"

const { body, divider } = style

// An object's dimensions are inches of a real object: 0 is not a smaller object, it's a broken one.
const MIN_DIMENSION_IN = 1

// The super/object levels are never rendered for "Other" (CategoriesSection hides them once the top
// level is "Other"), so there is no list to fetch for them. Hoisted so the empty array and the
// no-op handler don't churn on every render.
const NO_CATEGORY_OPTIONS: DefaultOptionType[] = []
const noop = () => {}

type Props = {
	/**
	 * The area drawn on the drawing, or null while the user still has to draw one — which is the
	 * difference between the panel's two states: the instruction, or the form + footer.
	 */
	area: { pageNumber: number } | null
	/** Elevation references of the page the area was drawn on (erefs are page-bound). */
	pageErefs: TEref[]
	isPending?: boolean
	onCancel: () => void
	onCreate: (details: TOtherCreateDetails) => void
	onClose: () => void
}

/**
 * Right-panel companion of the "add other object" mode (REQ-299), rendered through the shared
 * `CreateAreaSidebar` shell: before an area is drawn it only explains the gesture, and once one
 * exists it asks for the object's details.
 *
 * The form is deliberately the furniture EDIT sidebar's own sections, in the design's order and
 * with nothing else: image, elevation reference, dimensions, name, price, categories. Which
 * sections appear is the same split the edit sidebar makes for an "Other" item — no model number
 * and no attachments, but a name and a price instead — so the panel the user creates in and the
 * panel they land in afterwards read as one.
 *
 * The categories block is a label, not a question: an "Other" object is BY DEFINITION the one with
 * no category on any level, so the top level is locked to "Other" and the two below it are not
 * rendered at all. Their absence IS what the mode creates.
 */
export const CreateOtherSidebar = ({
	area,
	pageErefs,
	isPending = false,
	onCancel,
	onCreate,
	onClose,
}: Props) => {
	const form = useCreateOtherForm(pageErefs)

	// First field of the form, so the shell puts the caret in it the moment the drag lands and the
	// user can carry straight on from the mouse without an extra click.
	const elevationFieldRef = useRef<HTMLDivElement>(null)

	const canCreate = !!area && form.isValid && !isPending

	const handleCreate = () => {
		// Re-checked rather than trusted: `canCreate` is what the button reads, this is what narrows
		// the nullable form fields for the payload.
		if (!canCreate || form.width === null || form.height === null || form.depth === null || form.price === null) {
			return
		}
		onCreate({
			width: form.width,
			height: form.height,
			depth: form.depth,
			name: form.name.trim(),
			price: form.price,
			eref: form.selectedEref ? { id: form.selectedEref.id, version: form.selectedEref.version } : null,
			// Null while the section is collapsed or incomplete, which is what keeps an untouched
			// section from adding a request (REQ-413/414).
			customization: form.customization.payload,
		})
	}

	return (
		<CreateAreaSidebar
			title="Object"
			hint="Drag on the drawing to define the object area"
			area={area}
			isPending={isPending}
			canSubmit={canCreate}
			submitLabel="Create object"
			onCancel={onCancel}
			onClose={onClose}
			onSubmit={handleCreate}
			focusTargetRef={elevationFieldRef}
		>
			<div className={body}>
				<FurnitureImage imageUrl={null} isCountertop={false} hasModelNumber={false} isOther />

				<div ref={elevationFieldRef}>
					<ElevationReferenceSelect
						erefs={pageErefs}
						value={form.selectedErefId}
						onChange={form.setSelectedErefId}
						required={false}
						placeholder="Select elevation"
					/>
				</div>

				<div className={divider} />

				<DimensionsSection
					width={form.width}
					height={form.height}
					depth={form.depth}
					onWidthChange={form.setWidth}
					onHeightChange={form.setHeight}
					onDepthChange={form.setDepth}
					min={MIN_DIMENSION_IN}
				/>

				<div className={divider} />

				<NameSection value={form.name} onChange={form.setName} />

				<PriceSection value={form.price} onChange={form.setPrice} />

				<div className={divider} />

				<CustomizationSection
					isExpanded={form.customization.isExpanded}
					note={form.customization.note}
					priceAdjustment={form.customization.priceAdjustment}
					onExpand={form.customization.expand}
					onRemove={form.customization.collapse}
					onNoteChange={form.customization.setNote}
					onPriceAdjustmentChange={form.customization.setPriceAdjustment}
				/>

				<div className={divider} />

				<CategoriesSection
					topLevelValue={OTHER_TOP_LEVEL_CATEGORY}
					topLevelOptions={OTHER_TOP_LEVEL_OPTIONS}
					onTopLevelChange={noop}
					superValue={null}
					superOptions={NO_CATEGORY_OPTIONS}
					onSuperChange={noop}
					modelCategoryValue={null}
					modelCategoryOptions={NO_CATEGORY_OPTIONS}
					onModelCategoryChange={noop}
					topLevelDisabled
				/>
			</div>
		</CreateAreaSidebar>
	)
}
